import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize, forkJoin, Subscription } from 'rxjs';
import { DealersService } from '../../dealers.service';
import { Action, FirmType } from '../../../common/models/common.model';
import { Dealer } from '../../dealers.model';
import { EmployeesService } from '../../../employees/employees.service';
import { EmployeeBasicInfo, SalesOfficersRole } from '../../../employees/employees.model';

@Component({
  selector: 'app-dealers-info-form',
  standalone: false,
  templateUrl: './dealers-info-form.component.html',
  styleUrl: './dealers-info-form.component.scss',
})
export class DealersInfoFormComponent implements OnInit, OnDestroy {
  @Input() dealer: Dealer | null = null;
  @Input() mode: Action = Action.CREATE;
  @Output() onCancelEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() onSaveEvent: EventEmitter<Dealer> = new EventEmitter<Dealer>();

  protected dealerForm!: FormGroup;
  protected isDealersDataSaving: boolean = false;
  protected formError: any = null;

  protected firmTypeOptions = [
    { label: 'Sole Proprietorship', value: FirmType.SOLE_PROPRIETORSHIP },
    { label: 'Partnership Firm', value: FirmType.PARTNERSHIP_FIRM },
    { label: 'Limited Liability Partnership (LLP)', value: FirmType.LLP },
    { label: 'Private Limited Company (Pvt. Ltd.)', value: FirmType.PRIVATE_LIMITED },
    { label: 'Public Limited Company', value: FirmType.PUBLIC_LIMITED },
    { label: 'One Person Company (OPC)', value: FirmType.OPC },
    { label: 'Co-operative Society', value: FirmType.CO_OPERATIVE_SOCIETY },
    { label: 'Trust / Society', value: FirmType.TRUST_SOCIETY },
  ];

  protected areaSalesOfficers: EmployeeBasicInfo[] = [];
  protected regionalSalesOfficers: EmployeeBasicInfo[] = [];
  protected salesOfficers: EmployeeBasicInfo[] = [];
  protected isLoading: boolean = false;

  private _dealerSubscription!: Subscription;

  constructor(
    private _dealersService: DealersService,
    private employeeService: EmployeesService
  ) {}

  ngOnInit(): void {
    this.getEmployees();
  }

  private getEmployees(): void {
    this.isLoading = true;
    const salesOfficer$ = this.employeeService.getSalesUsersList(SalesOfficersRole.SALES_OFFICERS);
    const areaSalesOfficers = this.employeeService.getSalesUsersList(SalesOfficersRole.AREA_SALES_MANAGERS);
    const regionalSalesOfficers = this.employeeService.getSalesUsersList(SalesOfficersRole.REGIONAL_SALES_MANAGERS);

    forkJoin([salesOfficer$, areaSalesOfficers, regionalSalesOfficers])
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: ([salesOfficers, areaSalesOfficers, regionalSalesOfficers]: [
          EmployeeBasicInfo[],
          EmployeeBasicInfo[],
          EmployeeBasicInfo[],
        ]) => {
          this.salesOfficers = salesOfficers;
          this.areaSalesOfficers = areaSalesOfficers;
          this.regionalSalesOfficers = regionalSalesOfficers;
          this._initializeEmployeeForm();
        },
      });
  }

  protected onSubmitDealerForm(): void {
    if (this.dealerForm.invalid) {
      return;
    }

    this.isDealersDataSaving = true;

    const reqBody = {
      ...this.dealerForm.value,
    };

    const request$ =
      this.dealer && this.mode === Action.EDIT
        ? this._dealersService.update(this.dealer.id, reqBody)
        : this._dealersService.post(reqBody);

    this._dealerSubscription = request$
      .pipe(
        finalize(() => {
          this.isDealersDataSaving = false;
        })
      )
      .subscribe({
        next: (dealerInfo: Dealer) => {
          this.formError = null;
          this.onSaveEvent.emit(dealerInfo);
        },
        error: error => {
          this.formError = error?.error;
        },
      });
  }

  protected onCancel(): void {
    this.onCancelEvent.emit();
  }

  private _initializeEmployeeForm(): void {
    this.dealerForm = new FormGroup({
      first_name: new FormControl(this.dealer?.first_name ?? null, Validators.required),
      last_name: new FormControl(this.dealer?.last_name ?? null, Validators.required),
      email: new FormControl(this.dealer?.email ?? null, [Validators.email]),
      phone_number: new FormControl(this.dealer?.phone_number ?? null, Validators.required),
      shop_name: new FormControl(this.dealer?.shop_name ?? null, Validators.required),
      gst_number: new FormControl(this.dealer?.gst_number ?? null),
      pan_card_number: new FormControl(this.dealer?.pan_card_number ?? null),
      date_of_birth: new FormControl(this.dealer?.date_of_birth ?? null),
      firm_type: new FormControl(this.dealer?.firm_type ?? null, Validators.required),
      sales_officer_id: new FormControl(this.dealer?.sales_officer?.id ?? null),
      area_sales_manager_id: new FormControl(this.dealer?.area_sales_manager?.id ?? null),
      regional_sales_manager_id: new FormControl(this.dealer?.regional_sales_manager?.id ?? null),
      address_line_1: new FormControl(this.dealer?.address_line_1 ?? null, Validators.required),
      district: new FormControl(this.dealer?.district ?? null, Validators.required),
      state: new FormControl(this.dealer?.state ?? null, Validators.required),
      pincode: new FormControl(this.dealer?.pincode ?? null, Validators.required),
    });
  }

  ngOnDestroy(): void {
    this._dealerSubscription?.unsubscribe();
  }
}
